// hooks/useTabTitle.ts
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import type { RootState } from '../store'
import { selectActiveMailboxQuestionCount, selectActiveMailboxUnreadCount } from 'features/agentic-hub/store/selectors'
import { useBoxTruckProfilesMode } from 'features/box-truck-loads/hooks/useBoxTruckProfilesMode'
import { buildTabTitle } from '../constants/routeTitles'
import { useNotification } from './useNotification'

/**
 * Hook to dynamically update the browser tab title with a combined notification count.
 *
 * The title names the page on screen (`constants/routeTitles`) — e.g. "Dispatch Board | Numeo" — so several
 * open Numeo tabs can be told apart. A count sums global app notifications with the SELECTED AI Hub mailbox's
 * pending questions/approvals and unread broker emails — e.g. "(7) Dispatch Board | Numeo" — so the number matches the
 * mailbox the dispatcher is viewing (not a cross-mailbox total). Gated on the AI Hub page being open
 * (the selectors return 0 otherwise). This is the single owner of `document.title`, so no competing
 * effect can clobber it.
 *
 * @returns void
 */
const useTabTitle = (): void => {
    const { notifications } = useNotification()
    const { pathname } = useLocation()
    const boxTruckProfilesMode = useBoxTruckProfilesMode()
    const questionCount = useSelector((s: RootState) => selectActiveMailboxQuestionCount(s))
    const unreadCount = useSelector((s: RootState) => selectActiveMailboxUnreadCount(s))

    useEffect(() => {
        const title = buildTabTitle(pathname, { boxTruckProfilesMode })
        const count = notifications.length + questionCount + unreadCount
        document.title = count > 0 ? `(${count}) ${title}` : title

        // Cleanup function to reset title when component unmounts
        return () => {
            document.title = title
        }
    }, [notifications, questionCount, unreadCount, pathname, boxTruckProfilesMode])
}

export default useTabTitle
